'use client';

import clsx from 'clsx';

import { PlayerSelect } from '@/components/atoms';
import { PlayerType } from '@/components/constants/enums';
import { SelectedAwayPlayers, SelectedHomePlayers, SwappedPlayers } from '@/components/constants/types';
import { TeamLeaguePlayerInfo } from '@/entities';

interface PlayersDropdownProps {
	players: TeamLeaguePlayerInfo[];
	onPlayerSelect: (selectedPlayerId: string | number, playerType: PlayerType) => void;
	isLocked?: boolean;
	selectedPlayers: SelectedHomePlayers | SelectedAwayPlayers;
	isDoubleMatch: boolean;
	swapperOutPlayers?: SwappedPlayers['teamOne'] | SwappedPlayers['teamTwo'];
	showOponentLineup?: boolean;
	shouldHideDropdown: boolean;
	isHome?: boolean;
}

export const PlayersDropdown = ({
	players,
	onPlayerSelect,
	isLocked = false,
	selectedPlayers,
	isDoubleMatch = false,
	swapperOutPlayers,
	showOponentLineup,
	shouldHideDropdown,
	isHome
}: PlayersDropdownProps) => {
	const handleSelect = (selectedPlayerId: string | number, playerType: PlayerType) => {
		onPlayerSelect(selectedPlayerId, playerType);
	};

	const isHomeTeam = (players: SelectedHomePlayers | SelectedAwayPlayers): players is SelectedHomePlayers => {
		return 'teamOnePlayerOneUuid' in players;
	};

	const getPlayerByUuid = (playerUuid: string | null) => players.find((player) => player.userUuid === playerUuid);

	const getSelectedFirstPlayer = () => {
		const playerUuid = isHomeTeam(selectedPlayers) ? selectedPlayers.teamOnePlayerOneUuid : selectedPlayers.teamTwoPlayerOneUuid;
		return getPlayerByUuid(playerUuid);
	};

	const getSelectedSecondPlayer = () => {
		const playerUuid = isHomeTeam(selectedPlayers) ? selectedPlayers.teamOnePlayerTwoUuid : selectedPlayers.teamTwoPlayerTwoUuid;
		return getPlayerByUuid(playerUuid);
	};

	return (
		<div className={clsx('flex flex-col gap-4 sm:flex-row sm:gap-8', isDoubleMatch ? 'w-full' : 'justify-start sm:w-1/2')}>
			{!shouldHideDropdown ? (
				<>
					<PlayerSelect
						players={players}
						onSelect={(selectedPlayerId) => handleSelect(selectedPlayerId, PlayerType.PLAYER_ONE)}
						selectedPlayer={getSelectedFirstPlayer()}
						selectedPlayerRawName={
							isHomeTeam(selectedPlayers) ? selectedPlayers.teamOnePlayerOneName : selectedPlayers.teamTwoPlayerOneName
						}
						playerLabel="Player 1"
						placeholder={getSelectedFirstPlayer()?.fullName || 'Select Player 1'}
						isLocked={isLocked}
						swapperOutPlayer={swapperOutPlayers?.playerOne}
						isBlured={!showOponentLineup}
					/>

					{isDoubleMatch && (
						<PlayerSelect
							players={players}
							onSelect={(selectedPlayerId) => handleSelect(selectedPlayerId, PlayerType.PLAYER_TWO)}
							selectedPlayer={getSelectedSecondPlayer()}
							selectedPlayerRawName={
								isHomeTeam(selectedPlayers) ? selectedPlayers.teamOnePlayerTwoName : selectedPlayers.teamTwoPlayerTwoName
							}
							playerLabel="Player 2"
							placeholder={getSelectedSecondPlayer()?.fullName || 'Select Player 2'}
							isLocked={isLocked}
							swapperOutPlayer={swapperOutPlayers?.playerTwo}
							isBlured={!showOponentLineup}
						/>
					)}
				</>
			) : (
				<p className="text-gray-600 text-sm font-semibold">{isHome ? 'Away' : 'Home'} team needs to select lineups first</p>
			)}
		</div>
	);
};
